import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { HistoricNarService } from "../../../../../../shared/api/services/historicNar/historicNarService";
import type { HistoricNarRecommendation } from "../../../../../../shared/api/services/historicNar/types";
import { logError } from "../../../../../utils/customLogger";

/** Load the full recommendation set and cancel transport work on real unmounts. */
const useHistoricNarRecommendations = () => {
  const hasStartedInitialFetch = useRef(false);
  const isMounted = useRef(false);
  const activeRequest = useRef<AbortController>();
  const [rows, setRows] = useState<HistoricNarRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchRecommendations = useCallback(
    async (options: { silent?: boolean } = {}) => {
      activeRequest.current?.abort();
      const controller = new AbortController();
      activeRequest.current = controller;
      setIsLoading(true);
      try {
        const recommendations = await HistoricNarService.listAllRecommendations(
          {},
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setRows(recommendations);
        if (!recommendations.length) {
          toast.error("No historic NAR recommendations found");
        } else if (!options.silent) {
          toast.success("Historic NAR recommendations loaded");
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        logError(error as Error, "Error fetching historic NAR recommendations");
        toast.error("Error loading historic NAR recommendations");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setHasFetched(true);
        }
        if (activeRequest.current === controller)
          activeRequest.current = undefined;
      }
    },
    [],
  );

  useEffect(() => {
    isMounted.current = true;
    if (!hasStartedInitialFetch.current) {
      hasStartedInitialFetch.current = true;
      void fetchRecommendations();
    }
    return () => {
      isMounted.current = false;
      void Promise.resolve().then(() => {
        if (!isMounted.current) activeRequest.current?.abort();
      });
    };
  }, [fetchRecommendations]);

  return {
    rows,
    isLoading,
    hasFetched,
    refresh: fetchRecommendations,
    refreshSilently: useCallback(
      () => fetchRecommendations({ silent: true }),
      [fetchRecommendations],
    ),
  };
};

export default useHistoricNarRecommendations;
